import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shareLocation } from '../services/shareLocation.service';
import { useBrandedImages } from '../hooks/useBranding';

const styles = {
  body: {
    fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif`,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    minHeight: '100vh',
    margin: 0,
    padding: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: '100%',
    maxWidth: 400,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    padding: '2rem',
    textAlign: 'center',
  },
  header: {
    color: '#2d3748',
    fontSize: '1.75rem',
    fontWeight: 700,
    marginBottom: '1.5rem',
    lineHeight: 1.2,
  },
  logo: {
    display: 'block',
    margin: '0 auto 1.5rem auto',
    height: 60,
    width: 'auto',
  },
  btn: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    padding: '1rem 2rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '1.5rem',
    width: '100%',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    userSelect: 'none',
  },
  status: {
    marginTop: '1.5rem',
    textAlign: 'center',
    fontSize: '1rem',
    minHeight: '3rem',
    padding: '1rem',
    borderRadius: 8,
    background: '#f7fafc',
    border: '1px solid #e2e8f0',
  },
  statusError: {
    background: '#fed7d7',
    borderColor: '#feb2b2',
    color: '#c53030',
  },
  statusSuccess: {
    background: '#c6f6d5',
    borderColor: '#9ae6b4',
    color: '#22543d',
  },
  statusInfo: {
    background: '#bee3f8',
    borderColor: '#90cdf4',
    color: '#2a4365',
  },
  permissionPrompt: {
    background: '#fff3cd',
    border: '1px solid #ffeaa7',
    color: '#856404',
    padding: '1rem',
    borderRadius: 8,
    margin: '1rem 0',
    fontSize: '0.9rem',
    lineHeight: 1.4,
  },
  locationIcon: {
    width: 48,
    height: 48,
    margin: '0 auto 1rem auto',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: 24,
  },
  hidden: {
    display: 'none',
  },
};

function getStatusStyle(type) {
  if (type === 'error') return { ...styles.status, ...styles.statusError };
  if (type === 'success') return { ...styles.status, ...styles.statusSuccess };
  return { ...styles.status, ...styles.statusInfo };
}

const ShareMyLocation = () => {
  const [searchParams] = useSearchParams();
  const qPhone = searchParams.get('phone');
  const qcaseId = searchParams.get('caseId');

  const [status, setStatus] = useState({ msg: '', type: 'info' });
  const [loading, setLoading] = useState(false);
  const [permissionPrompt, setPermissionPrompt] = useState(false);
  const [shared, setShared] = useState(false);
  
  // Get branded logo
  const { logo } = useBrandedImages();

  const setStatusMsg = (msg, type = 'info') => setStatus({ msg, type });

  const handleShare = useCallback(() => {
    if (!navigator.geolocation) {
      setStatusMsg('Geolocation is not supported by your browser.', 'error');
      return;
    }
    if (!qPhone) {
      setStatusMsg('Missing phone number parameter. Please check the link.', 'error');
      return;
    }
    if (!qcaseId) {
      setStatusMsg('Missing case ID parameter. Please check the link.', 'error');
      return;
    }
    setLoading(true);
    setStatusMsg('Requesting your location...', 'info');
    setPermissionPrompt(false);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setStatusMsg('Sending your location...', 'info');
        try {
          const result = await shareLocation({
            mobile_number: qPhone,
            case_id: parseInt(qcaseId),
            lat: position.coords.latitude,
            long: position.coords.longitude,
          });
          if (result.status === 'success') {
            setStatusMsg('Location shared successfully! Thank you.', 'success');
            setShared(true);
          } else {
            throw new Error(result.detail || result.message || 'Failed to share location');
          }
        } catch (err) {
          if (err.message.includes('Mobile number verification failed')) {
            setStatusMsg('Mobile number verification failed. Please contact support.', 'error');
          } else if (err.message.includes('Case not found')) {
            setStatusMsg('Case not found. Please check the link or contact support.', 'error');
          } else {
            setStatusMsg('Error sending location. Please try again.', 'error');
          }
        }
        setLoading(false);
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setStatusMsg('Location permission denied.', 'error');
            setPermissionPrompt(true);
            break;
          case error.POSITION_UNAVAILABLE:
            setStatusMsg('Location information is unavailable. Please check your GPS settings.', 'error');
            break;
          case error.TIMEOUT:
            setStatusMsg('Location request timed out. Please try again.', 'error');
            break;
          default:
            setStatusMsg('An unknown error occurred while getting your location.', 'error');
        }
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 300000,
      }
    );
  }, [qPhone, qcaseId]);

  // Check geolocation support and permission on mount
  React.useEffect(() => {
    if (!navigator.geolocation) {
      setStatusMsg('Geolocation is not supported by your browser.', 'error');
      return;
    }
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
        if (permissionStatus.state === 'denied') {
          setPermissionPrompt(true);
        }
        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'granted') {
            setPermissionPrompt(false);
            setStatusMsg('Location permission granted. You can now share your location.', 'success');
          } else if (permissionStatus.state === 'denied') {
            setPermissionPrompt(true);
          }
        };
      });
    }
  }, []);

  // Recheck permission when page becomes visible
  React.useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && navigator.geolocation) {
        if (navigator.permissions && navigator.permissions.query) {
          navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
            if (permissionStatus.state === 'denied') {
              setPermissionPrompt(true);
            } else {
              setPermissionPrompt(false);
            }
          });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  return (
    <div style={styles.body}>
      <main style={styles.container} aria-labelledby="main-header">
        <div style={styles.locationIcon}>📍</div>
        <img src={logo} alt="Company Logo" style={styles.logo} />
        <h1 style={styles.header} id="main-header">Share My Location</h1>
        {permissionPrompt && (
          <div style={styles.permissionPrompt}>
            <strong style={{ color: '#d63031' }}>Location Permission Required</strong><br />
            Please allow location access when prompted by your browser. This helps us provide better roadside assistance.
          </div>
        )}
        <button
          style={styles.btn}
          type="button"
          onClick={handleShare}
          disabled={loading || shared}
        >
          {loading ? (
            <span>
              <span style={{ display: 'inline-block', width: 20, height: 20, border: '3px solid rgba(255,255,255,.3)', borderRadius: '50%', borderTopColor: '#fff', animation: 'spin 1s ease-in-out infinite', marginRight: 8 }} />
              Getting Location...
            </span>
          ) : (
            'Share Location'
          )}
        </button>
        <div style={getStatusStyle(status.type)} role="status" aria-live="polite">{status.msg}</div>
      </main>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ShareMyLocation; 